import { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Globe, Sun, Moon } from "lucide-react";
import { SUPPORTED_LOCALE_CODES } from "@/utils/languageUtils";
// Lazy-loaded so Tailwind (pulled in via app.css from this modal's deps)
// doesn't ship in the marketing chunk until the user actually opens it.
const LanguageSelectionModal = lazy(() => import("@/components/login/LanguageSelectionModal"));
import { useLanguage } from "@/contexts/LanguageContext";
import { useThemeMode } from "@/contexts/ThemeContext";
import { getLanguageByCode } from "@/utils/languageUtils";
import FAQLink from "@/components/faq/FAQLink";
import { HeroChatPreview } from "@/components/askguiri/HeroChatPreview";

import turtleWaitingRoom from "@/assets/landing/turtle-waiting-room-960.webp";
import turtleWaitingRoomMd from "@/assets/landing/turtle-waiting-room-480.webp";
import turtleWaitingRoomSm from "@/assets/landing/turtle-waiting-room-240.webp";
import turtleReception from "@/assets/landing/turtle-reception-960.webp";
import turtleReceptionMd from "@/assets/landing/turtle-reception-480.webp";
import turtleReceptionSm from "@/assets/landing/turtle-reception-240.webp";
import turtlePlaza from "@/assets/landing/turtle-plaza-960.webp";
import turtlePlazaMd from "@/assets/landing/turtle-plaza-480.webp";
import turtlePlazaSm from "@/assets/landing/turtle-plaza-240.webp";
import turtlePlanDelDia from "@/assets/landing/turtle-plan-del-dia-960.webp";
import turtlePlanDelDiaMd from "@/assets/landing/turtle-plan-del-dia-480.webp";
import turtlePlanDelDiaSm from "@/assets/landing/turtle-plan-del-dia-240.webp";
import iconUrl from "@/assets/icons/icon-64.webp";
import caveatLatinFont from "@/assets/fonts/WnznHAc5bAfYB2QRah7pcpNvOx-pjcB9eIWpYQ.woff2";
import caveatLatinExtFont from "@/assets/fonts/WnznHAc5bAfYB2QRah7pcpNvOx-pjcB9eIupYSxP.woff2";
import "./MarketingLanding.css";

interface Freshness {
  boe: string | null;
  aeat: string | null;
  segSocial: string | null;
  extranjeria: string | null;
}

const APP_STORE_URL = "https://apps.apple.com/us/app/guiri/id6759870393";
const APP_STORE_BADGE =
  "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?releaseDate=1700000000";

// Caveat 500 (latin + latin-ext). Injected as a <style> after first paint by
// the effect in MarketingLanding — kept out of fonts-marketing.css so its
// woff2 stays off the inlined critical CSS / LCP path.
const CAVEAT_FONT_FACE =
  `@font-face{font-family:'Caveat';font-style:normal;font-weight:500;font-display:swap;src:url(${caveatLatinFont}) format('woff2');unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD;}` +
  `@font-face{font-family:'Caveat';font-style:normal;font-weight:500;font-display:swap;src:url(${caveatLatinExtFont}) format('woff2');unicode-range:U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF;}`;

// Tiny helper for translation strings that contain inline <em>, <strong>, <br>.
const Html = ({ as: Tag = "span", html, ...rest }: { as?: any; html: string } & Record<string, any>) => (
  <Tag {...rest} dangerouslySetInnerHTML={{ __html: html }} />
);

const MarketingLanding = () => {
  const { t, i18n } = useTranslation();
  const tk = (k: string) => t(`marketingLanding.${k}`);
  const lang = (i18n.language || "en").split("-")[0];
  const SITE = "https://guiri.app";

  const { currentLanguage } = useLanguage();
  const [langOpen, setLangOpen] = useState(false);
  const { theme, toggleTheme } = useThemeMode();

  // Caveat is decorative-only (the four .gl-turtle-cap figcaptions). Inject
  // its @font-face after first paint so the woff2 stays off the LCP critical
  // path; captions render in the cursive fallback until it loads.
  useEffect(() => {
    const inject = () => {
      if (document.getElementById("gl-caveat-font")) return;
      const style = document.createElement("style");
      style.id = "gl-caveat-font";
      style.textContent = CAVEAT_FONT_FACE;
      document.head.appendChild(style);
    };
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(inject);
    } else {
      setTimeout(inject, 200);
    }
  }, []);

  const currentLang = getLanguageByCode(currentLanguage);
  const langLabel = (currentLang?.code || "en").toUpperCase();

  const useCards = [0, 1, 2, 3].map((i) => ({
    tag: tk(`uses.cards.${i}.tag`),
    title: tk(`uses.cards.${i}.title`),
    desc: tk(`uses.cards.${i}.desc`),
  }));

  // 30 + 17 + 8 = 55 official Spanish sources, grouped by tier.
  // Source-of-truth for the corpus → mirrors the marketing landing brief.
  const nationalPills: [string, string][] = [
    ["AEAT", "Tax agency"],
    ["Hacienda", "Min. of Finance"],
    ["SEPE", "Employment"],
    ["Seguridad Social", "TGSS / INSS"],
    ["Extranjería", "Immigration"],
    ["DGT", "Traffic / driving"],
    ["BOE", "Official law gazette"],
    ["Interior", "Min. of Interior"],
    ["Exteriores", "Min. of Foreign Affairs"],
    ["Justicia", "Min. of Justice"],
    ["Inclusión", "Migration & inclusion"],
    ["Sanidad", "Min. of Health"],
    ["Educación", "Min. of Education"],
    ["MITECO", "Ecological transition"],
    ["MITES", "Min. of Labour"],
    ["Consumo", "Consumer affairs"],
    ["Comercio", "Trade & tourism"],
    ["Policía", "National police"],
    ["FNMT", "Digital certificates"],
    ["Catastro", "Land registry"],
    ["Registradores", "Property registry"],
    ["Notariado", "Notaries"],
    ["AEPD", "Data protection"],
    ["CNMC", "Markets & competition"],
    ["INCIBE", "Cybersecurity"],
    ["OEPM", "Patents & trademarks"],
    ["Trib. Constitucional", "Constitutional court"],
    ["Poder Judicial", "Judiciary"],
    ["Congreso", "Parliament"],
    ["Cl@ve · Carpeta", "Citizen e-gov"],
  ];

  const acPills: [string, string][] = [
    ["Madrid", "Comunidad de Madrid"],
    ["Cataluña", "Gencat · DOGC"],
    ["Andalucía", "Junta de Andalucía"],
    ["Valencia", "GVA · DOGV"],
    ["Euskadi", "Basque gov"],
    ["Galicia", "Xunta"],
    ["Murcia", "CARM"],
    ["Aragón", "Gobierno de Aragón"],
    ["Asturias", "Principado"],
    ["Balears", "Govern · BOIB"],
    ["Canarias", "Gobierno de Canarias"],
    ["Cantabria", "Gobierno · BOC"],
    ["Castilla-La Mancha", "JCCM · DOCM"],
    ["Castilla y León", "JCyL · BOCYL"],
    ["Extremadura", "Junta · DOE"],
    ["La Rioja", "Gobierno · BOR"],
    ["Navarra", "Gobierno · BON"],
  ];

  const cityHallPills: [string, string][] = [
    ["Madrid", "Ayuntamiento"],
    ["Barcelona", "Ajuntament"],
    ["Valencia", "Ayuntamiento"],
    ["Sevilla", "Ayuntamiento"],
    ["Málaga", "Ayuntamiento"],
    ["Zaragoza", "Ayuntamiento"],
    ["Alicante", "Ayuntamiento"],
    ["Las Palmas", "Ayuntamiento"],
  ];

  const compareRows = [0, 1, 2, 3, 4].map((i) => ({
    key: tk(`compare.rows.${i}.key`),
    a: tk(`compare.rows.${i}.a`),
    c: tk(`compare.rows.${i}.c`),
  }));

  const coverageItems = Array.from({ length: 14 }, (_, i) => tk(`coverage.items.${i}`));

  const faqItems = [0, 1, 2, 3, 4, 5].map((i) => ({
    q: tk(`faq.items.${i}.q`),
    a: tk(`faq.items.${i}.a`),
  }));

  const [freshness, setFreshness] = useState<Freshness | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Dynamic import keeps the Supabase client (~35 KiB) out of the
        // initial marketing bundle — freshness is non-critical, loads after paint.
        const { supabase } = await import("@/integrations/supabase/client");
        const { data, error } = await supabase.functions.invoke("marketing-freshness", {
          method: "GET",
        });
        if (cancelled || error || !data) return;
        setFreshness(data as Freshness);
      } catch {
        // Fail silent — widget hides gracefully if the endpoint is unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const freshnessRows: Array<{ key: keyof Freshness; label: string; desc: string }> = [
    { key: "boe", label: tk("freshness.boeLabel"), desc: tk("freshness.boeDesc") },
    { key: "aeat", label: tk("freshness.aeatLabel"), desc: tk("freshness.aeatDesc") },
    { key: "segSocial", label: tk("freshness.segSocialLabel"), desc: tk("freshness.segSocialDesc") },
    { key: "extranjeria", label: tk("freshness.extranjeriaLabel"), desc: tk("freshness.extranjeriaDesc") },
  ];

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="guiri-landing">
      <Helmet>
        <html lang={lang} />
        <title>{tk("seo.title")}</title>
        <meta name="description" content={tk("seo.description")} />
        <meta name="keywords" content={tk("seo.keywords")} />
        <link rel="canonical" href={SITE + "/"} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Guiri" />
        <meta property="og:url" content={SITE + "/"} />
        <meta property="og:locale" content={lang.replace("en", "en_US").replace("es", "es_ES").replace("fr", "fr_FR").replace("de", "de_DE").replace("it", "it_IT").replace("pt", "pt_PT").replace("uk", "uk_UA").replace("ro", "ro_RO").replace("ru", "ru_RU").replace("pl", "pl_PL").replace("nl", "nl_NL")} />
        <meta property="og:title" content={tk("seo.ogTitle")} />
        <meta property="og:description" content={tk("seo.ogDescription")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tk("seo.ogTitle")} />
        <meta name="twitter:description" content={tk("seo.ogDescription")} />
        {SUPPORTED_LOCALE_CODES.map((c) => (
          <link key={c} rel="alternate" hrefLang={c} href={`${SITE}/?lang=${c}`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={SITE + "/"} />
        {/* Preload the above-the-fold hero image so it competes for LCP */}
        <link
          rel="preload"
          as="image"
          href={turtleReception}
          // @ts-ignore — imageSrcSet/imageSizes are valid HTML5 but TS doesn't know them yet
          imageSrcSet={`${turtleReceptionSm} 240w, ${turtleReceptionMd} 480w, ${turtleReception} 960w`}
          imageSizes="(max-width: 768px) 240px, 320px"
          type="image/webp"
        />
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>
      {/* Nav */}
      <header className="gl-nav">
        <div className="gl-wrap gl-nav-inner">
          <Link className="gl-logo" to="/" aria-label={tk("nav.logoAria")}>
            <span className="gl-logo-mark"><img src={iconUrl} width={64} height={64} alt="" decoding="async" /></span>
            <span className="gl-logo-wordmark">guiri<span className="gl-logo-dot">.</span></span>
          </Link>
          <nav className="gl-nav-links">
            <a href="#how">{tk("nav.how")}</a>
            <a href="#uses">{tk("nav.uses")}</a>
            <a href="#sources">{tk("nav.sources")}</a>
            <a href="#pricing">{tk("nav.pricing")}</a>
          </nav>
          <div className="gl-nav-actions">
            <button
              type="button"
              className="gl-lang-btn"
              onClick={() => setLangOpen(true)}
              aria-label={tk("nav.changeLanguage")}
            >
              <Globe size={14} aria-hidden="true" />
              {langLabel}
            </button>
            <button
              type="button"
              className="gl-theme-toggle"
              onClick={toggleTheme}
              aria-label={tk("nav.toggleTheme")}
            >
              {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
            </button>
            <Link className="gl-btn gl-btn-saffron" to="/login">
              {tk("nav.tryGuiri")}
            </Link>
          </div>
        </div>
      </header>
      <main>

      {/* Hero */}
      <section className="gl-hero">
        <figure className="gl-turtle-pin gl-pin-hero gl-tape-cream" aria-hidden="true">
          <img src={turtleReceptionMd} srcSet={`${turtleReceptionSm} 240w, ${turtleReceptionMd} 480w, ${turtleReception} 960w`} sizes="(max-width: 768px) 240px, 320px" alt="" width={220} height={260} fetchPriority="high" decoding="async" />
          <figcaption className="gl-turtle-cap">{tk("hero.turtleCap")}</figcaption>
        </figure>
        <div className="gl-wrap gl-hero-grid">
          <div>
            <span className="gl-kicker gl-mono">{tk("hero.kicker")}</span>
            <Html as="h1" className="gl-h1" html={tk("hero.h1Html")} />
            <p className="gl-lede">{tk("hero.lede")}</p>
            <div className="gl-hero-ctas">
              <Link className="gl-btn gl-btn-saffron gl-btn-lg" to="/login">
                {tk("hero.ctaPrimary")}
                <span aria-hidden="true">→</span>
              </Link>
              <a
                className="gl-app-store-link"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={tk("hero.appStoreAria")}
              >
                <img src={APP_STORE_BADGE} alt={tk("hero.appStoreAlt")} width={150} height={50} loading="lazy" decoding="async" />
              </a>
            </div>
            <div className="gl-hero-trust">
              <div>
                <div className="gl-hero-trust-stat">{tk("hero.trustStat")}</div>
                <div className="gl-hero-trust-label">English, Español, Français, Deutsch, Italiano, Português, Українська, Română, Русский, Polski, Nederlands</div>
              </div>
            </div>
            <div className="gl-hero-stats gl-hero-stats-4">
              <div className="gl-hero-stat"><span className="gl-hero-stat-num">{tk("hero.stat1Num")}</span><span className="gl-hero-stat-lbl">{tk("hero.stat1Label")}</span></div>
              <div className="gl-hero-stat"><span className="gl-hero-stat-num">{tk("hero.stat2Num")}</span><span className="gl-hero-stat-lbl">{tk("hero.stat2Label")}</span></div>
              <div className="gl-hero-stat"><span className="gl-hero-stat-num">{tk("hero.stat3Num")}</span><span className="gl-hero-stat-lbl">{tk("hero.stat3Label")}</span></div>
              <div className="gl-hero-stat"><span className="gl-hero-stat-num">{tk("hero.stat4Num")}</span><span className="gl-hero-stat-lbl">{tk("hero.stat4Label")}</span></div>
            </div>
          </div>

          <HeroChatPreview
            ariaLabel={tk("hero.chat.aria")}
            userText={tk("hero.chat.user1")}
            tldr={tk("hero.chat.tldr")}
            botHtml={tk("hero.chat.botHtml")}
          />
        </div>
      </section>

      {/* How it works */}
      <section className="gl-section" id="how">
        <figure className="gl-turtle-pin gl-pin-how" aria-hidden="true">
          <img src={turtlePlazaMd} srcSet={`${turtlePlazaSm} 240w, ${turtlePlazaMd} 480w, ${turtlePlaza} 960w`} sizes="(max-width: 768px) 240px, 320px" alt="" loading="lazy" decoding="async" />
          <figcaption className="gl-turtle-cap">{tk("how.turtleCap")}</figcaption>
        </figure>
        <div className="gl-wrap">
          <div className="gl-section-head gl-section-head-stack">
            <div>
              <span className="gl-kicker gl-mono">{tk("how.kicker")}</span>
              <Html as="h2" className="gl-h2 gl-h2-oneline" html={tk("how.h2Html")} />
              <p className="gl-section-head-meta">{tk("how.meta")}</p>
            </div>
          </div>
          <div className="gl-how-grid">
            {[1, 2, 3].map((i) => (
              <div className="gl-how-cell" key={i}>
                <div className="gl-how-step">{tk(`how.step${i}.step`)}</div>
                <h3 className="gl-how-title">{tk(`how.step${i}.title`)}</h3>
                <p className="gl-how-desc">{tk(`how.step${i}.desc`)}</p>
                {i === 1 && (
                  <div className="gl-illus gl-illus-drop" aria-hidden="true">
                    <div className="gl-illus-stamp">AEAT</div>
                    <div className="gl-illus-paper gl-paper-1">
                      <span className="gl-paper-line gl-w-70"></span>
                      <span className="gl-paper-line gl-w-90"></span>
                      <span className="gl-paper-line gl-w-50"></span>
                      <span className="gl-paper-line gl-w-80"></span>
                    </div>
                    <div className="gl-illus-paper gl-paper-2">
                      <span className="gl-paper-line gl-w-60"></span>
                      <span className="gl-paper-line gl-w-85"></span>
                      <span className="gl-paper-line gl-w-40"></span>
                    </div>
                    <div className="gl-illus-clip"></div>
                    <div className="gl-illus-meta">
                      <span className="gl-file-ico">PDF</span>
                      <span>carta_aeat.pdf</span>
                    </div>
                  </div>
                )}
                {i === 2 && (
                  <div className="gl-illus gl-illus-decode" aria-hidden="true">
                    <div className="gl-decode-col gl-decode-es">
                      <div className="gl-decode-tag">ES</div>
                      <span className="gl-paper-line gl-w-90"></span>
                      <span className="gl-paper-line gl-w-70"></span>
                      <span className="gl-paper-line gl-w-85"></span>
                      <span className="gl-paper-line gl-w-55"></span>
                    </div>
                    <div className="gl-decode-arrow">
                      <div className="gl-decode-router">
                        <span className="gl-router-dot"></span>
                        <span className="gl-router-dot gl-active"></span>
                        <span className="gl-router-dot"></span>
                      </div>
                      <div className="gl-decode-line"></div>
                      <div className="gl-decode-tip">▶</div>
                    </div>
                    <div className="gl-decode-col gl-decode-en">
                      <div className="gl-decode-tag gl-tag-saffron">{currentLanguage.toUpperCase()}</div>
                      <span className="gl-paper-line gl-w-80 gl-ink"></span>
                      <span className="gl-paper-line gl-w-95 gl-ink"></span>
                      <span className="gl-paper-line gl-w-60 gl-ink"></span>
                    </div>
                  </div>
                )}
                {i === 3 && (
                  <div className="gl-illus gl-illus-act" aria-hidden="true">
                    <div className="gl-act-row">
                      <span className="gl-act-check gl-done">✓</span>
                      <Html as="span" className="gl-act-label" html={tk("how.act.row1Html")} />
                      <span className="gl-act-pill">{tk("how.act.row1Pill")}</span>
                    </div>
                    <div className="gl-act-row">
                      <span className="gl-act-check gl-done">✓</span>
                      <span className="gl-act-label">{tk("how.act.row2")}</span>
                      <span className="gl-act-pill">{tk("how.act.row2Pill")}</span>
                    </div>
                    <div className="gl-act-row">
                      <span className="gl-act-check">→</span>
                      <span className="gl-act-label">sede.agenciatributaria.gob.es</span>
                      <span className="gl-act-pill gl-pill-ink">{tk("how.act.row3Pill")}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare */}
      <section className="gl-section" id="compare">
        <div className="gl-wrap">
          <div className="gl-section-head gl-section-head-stack">
            <div>
              <span className="gl-kicker gl-mono">{tk("compare.kicker")}</span>
              <Html as="h2" className="gl-h2" html={tk("compare.h2Html")} />
              <p className="gl-section-head-meta">{tk("compare.meta")}</p>
            </div>
          </div>

          {/* Desktop table */}
          <div className="gl-compare" role="table" aria-label={tk("compare.kicker")}>
            <div className="gl-compare-row is-head">
              <div className="gl-compare-cell"><span className="gl-compare-rowlabel">&nbsp;</span></div>
              <div className="gl-compare-cell">
                <div className="gl-compare-head">{tk("compare.head1")}</div>
                <div className="gl-compare-head-sub">{tk("compare.head1Sub")}</div>
              </div>
              <div className="gl-compare-cell gl-compare-col-guiri is-head">
                <div className="gl-compare-head">
                  <span className="gl-logo gl-logo-compare" aria-label="Guiri">
                    <span className="gl-logo-mark">
                      <img src={iconUrl} width={48} height={48} alt="" decoding="async" />
                    </span>
                    <span className="gl-logo-wordmark">guiri<span className="gl-logo-dot">.</span></span>
                  </span>
                </div>
                <div className="gl-compare-head-sub">{tk("compare.head3Sub")}</div>
              </div>
            </div>
            {compareRows.map((row) => (
              <div className="gl-compare-row" key={row.key}>
                <div className="gl-compare-cell"><span className="gl-compare-rowlabel">{row.key}</span></div>
                <div className="gl-compare-cell">{row.a}</div>
                <div className="gl-compare-cell gl-compare-col-guiri">{row.c}</div>
              </div>
            ))}
          </div>

          {/* Mobile stack */}
          <div className="gl-compare-stack">
            <div className="gl-compare-card">
              <div className="gl-compare-card-head">
                <div className="gl-compare-card-name">{tk("compare.head1")}</div>
                <div className="gl-compare-card-sub">{tk("compare.head1Sub")}</div>
              </div>
              <div className="gl-compare-card-list">
                {compareRows.map((row) => (
                  <div className="gl-compare-card-row" key={row.key}>
                    <div className="gl-compare-card-key">{row.key}</div>
                    <div className="gl-compare-card-val">{row.a}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="gl-compare-card is-guiri">
              <div className="gl-compare-card-head">
                <div className="gl-compare-card-name">
                  <span className="gl-logo gl-logo-compare" aria-label="Guiri">
                    <span className="gl-logo-mark">
                      <img src={iconUrl} width={48} height={48} alt="" decoding="async" />
                    </span>
                    <span className="gl-logo-wordmark">guiri<span className="gl-logo-dot">.</span></span>
                  </span>
                </div>
                <div className="gl-compare-card-sub">{tk("compare.head3Sub")}</div>
              </div>
              <div className="gl-compare-card-list">
                {compareRows.map((row) => (
                  <div className="gl-compare-card-row" key={row.key}>
                    <div className="gl-compare-card-key">{row.key}</div>
                    <div className="gl-compare-card-val">{row.c}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="gl-section" id="uses">
        <div className="gl-wrap">
          <div className="gl-section-head">
            <div>
              <span className="gl-kicker gl-mono">{tk("uses.kicker")}</span>
              <Html as="h2" className="gl-h2" html={tk("uses.h2Html")} />
            </div>
            <p className="gl-section-head-meta">{tk("uses.meta")}</p>
          </div>
          <div className="gl-uses">
            {useCards.map((c) => (
              <div className="gl-use-card" key={c.title}>
                <span className="gl-use-tag">{c.tag}</span>
                <h3 className="gl-use-title">{c.title}</h3>
                <p className="gl-use-desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sources */}
      <section className="gl-section" id="sources">
        <figure className="gl-turtle-pin gl-pin-sources gl-tape-blue" aria-hidden="true">
          <img src={turtleWaitingRoomMd} srcSet={`${turtleWaitingRoomSm} 240w, ${turtleWaitingRoomMd} 480w, ${turtleWaitingRoom} 960w`} sizes="(max-width: 768px) 240px, 320px" alt="" loading="lazy" decoding="async" />
          <figcaption className="gl-turtle-cap">{tk("sources.turtleCap")}</figcaption>
        </figure>
        <div className="gl-wrap">
          <div className="gl-section-head gl-section-head-stack">
            <div>
              <span className="gl-kicker gl-mono">{tk("sources.kicker")}</span>
              <Html as="h2" className="gl-h2" html={tk("sources.h2Html")} />
              <p className="gl-section-head-meta">{tk("sources.meta")}</p>
            </div>
          </div>

          {/* Tier 1: National */}
          <div className="gl-source-tier">
            <div className="gl-source-tier-head">
              <span className="gl-source-tier-title">{tk("sources.tier1.title")}</span>
              <span className="gl-source-tier-count">{tk("sources.tier1.count")}</span>
            </div>
            <div className="gl-sources-grid">
              {nationalPills.map(([n, k]) => (
                <div className="gl-src" key={`nat-${n}`}>
                  <div className="gl-src-name">{n}</div>
                  <div className="gl-src-kind">{k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tier 2: Autonomous communities */}
          <div className="gl-source-tier">
            <div className="gl-source-tier-head">
              <span className="gl-source-tier-title">{tk("sources.tier2.title")}</span>
              <span className="gl-source-tier-count">{tk("sources.tier2.count")}</span>
            </div>
            <div className="gl-sources-grid">
              {acPills.map(([n, k]) => (
                <div className="gl-src" key={`ac-${n}`}>
                  <div className="gl-src-name">{n}</div>
                  <div className="gl-src-kind">{k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tier 3: Major city halls */}
          <div className="gl-source-tier">
            <div className="gl-source-tier-head">
              <span className="gl-source-tier-title">{tk("sources.tier3.title")}</span>
              <span className="gl-source-tier-count">{tk("sources.tier3.count")}</span>
            </div>
            <div className="gl-sources-grid">
              {cityHallPills.map(([n, k]) => (
                <div className="gl-src" key={`city-${n}`}>
                  <div className="gl-src-name">{n}</div>
                  <div className="gl-src-kind">{k}</div>
                </div>
              ))}
            </div>
          </div>

          <p className="gl-fresh-cap" style={{ marginTop: 28 }}>{tk("sources.cap")}</p>

          {/* Freshness widget + framed brand photo */}
          {freshness && freshnessRows.some((r) => freshness[r.key]) && (
            <div className="gl-fresh-row-wrap">
              <div id="freshness" className="gl-fresh" aria-live="polite">
                <div className="gl-fresh-head">
                  <span className="gl-fresh-title">{tk("freshness.title")}</span>
                </div>
                <ul className="gl-fresh-list">
                  {freshnessRows
                    .filter((r) => freshness[r.key])
                    .map((r) => (
                      <li className="gl-fresh-row" key={r.key}>
                        <span className="gl-fresh-label">
                          {r.label} <em>· {r.desc}</em>
                        </span>
                        <span className="gl-fresh-when">{tk("freshness.fresh")}</span>
                      </li>
                    ))}
                </ul>
                <p className="gl-fresh-cap">{tk("freshness.caption")}</p>
              </div>

              <figure className="gl-frame">
                <img
                  className="gl-frame-photo"
                  src={turtlePlanDelDia}
                  srcSet={`${turtlePlanDelDiaSm} 240w, ${turtlePlanDelDiaMd} 480w, ${turtlePlanDelDia} 960w`}
                  sizes="(max-width: 480px) 240px, (max-width: 980px) 480px, 720px"
                  alt={tk("freshness.photoAlt")}
                  width={1536}
                  height={1024}
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="gl-frame-cap">{tk("freshness.photoCap")}</figcaption>
              </figure>
            </div>
          )}
        </div>
      </section>

      {/* Coverage map */}
      <section className="gl-section" id="coverage">
        <div className="gl-wrap">
          <div className="gl-section-head gl-section-head-stack">
            <div>
              <span className="gl-kicker gl-mono">{tk("coverage.kicker")}</span>
              <Html as="h2" className="gl-h2" html={tk("coverage.h2Html")} />
              <p className="gl-section-head-meta">{tk("coverage.meta")}</p>
            </div>
          </div>
          <div className="gl-coverage-grid">
            {coverageItems.map((name) => (
              <div className="gl-cov" key={name}>
                <div className="gl-cov-name">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="gl-section" id="pricing">
        <div className="gl-wrap">
          <div className="gl-section-head" style={{ justifyContent: "center", textAlign: "center", flexDirection: "column", alignItems: "center" }}>
            <span className="gl-kicker gl-mono">{tk("pricing.kicker")}</span>
            <Html as="h2" className="gl-h2" style={{ textAlign: "center" }} html={tk("pricing.h2Html")} />
            <p className="gl-section-head-meta" style={{ textAlign: "center" }}>{tk("pricing.meta")}</p>
          </div>
          <div className="gl-pricing">
            <div className="gl-price-card">
              <div className="gl-price-name">{tk("pricing.free.name")}</div>
              <p className="gl-price-tagline">{tk("pricing.free.tagline")}</p>
              <div className="gl-price-amount">
                <span className="gl-price-amount-num">€0</span>
              </div>
              <ul className="gl-price-list">
                <li>{tk("pricing.free.feat1")}</li>
                <li>{tk("pricing.free.feat2")}</li>
                <li>{tk("pricing.free.feat3")}</li>
                <li>{tk("pricing.free.feat4")}</li>
              </ul>
              
            </div>
            <div className="gl-price-card gl-pro">
              <span className="gl-price-tag">{tk("pricing.pro.tag")}</span>
              <div className="gl-price-name">{tk("pricing.pro.name")}</div>
              <p className="gl-price-tagline">{tk("pricing.pro.tagline")}</p>
              <div className="gl-price-amount">
                <span className="gl-price-amount-num">€5.99</span>
                <span className="gl-price-amount-unit">{tk("pricing.pro.unit")}</span>
              </div>
              <ul className="gl-price-list">
                <li className="gl-price-list-everything">{tk("pricing.pro.feat1")}</li>
                <li>{tk("pricing.pro.feat2")}</li>
                <li>{tk("pricing.pro.feat3")}</li>
                <li>{tk("pricing.pro.feat4")}</li>
                <li>{tk("pricing.pro.feat5")}</li>
              </ul>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
            <Link className="gl-btn gl-btn-saffron gl-btn-lg" to="/login">
              {tk("pricing.cta")} <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="gl-pricing-mission">
            <p>{tk("pricing.missionNote1")}</p>
            <p>{tk("pricing.missionNote2")}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="gl-section" id="faq">
        <div className="gl-wrap gl-faq">
          <div>
            <span className="gl-kicker gl-mono">{tk("faq.kicker")}</span>
            <Html as="h2" className="gl-h2" html={tk("faq.h2Html")} />
          </div>
          <div>
            {faqItems.map((f) => (
              <div className="gl-faq-item" key={f.q}>
                <h3 className="gl-faq-q">{f.q}</h3>
                <p className="gl-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="gl-final">
        <figure className="gl-turtle-pin gl-pin-final" aria-hidden="true">
          <img src={turtlePlazaMd} srcSet={`${turtlePlazaSm} 240w, ${turtlePlazaMd} 480w, ${turtlePlaza} 960w`} sizes="(max-width: 768px) 240px, 320px" alt="" loading="lazy" decoding="async" />
          <figcaption className="gl-turtle-cap">{tk("final.turtleCap")}</figcaption>
        </figure>
        <div className="gl-wrap" style={{ position: "relative" }}>
          <Html as="div" className="gl-final-stamp" aria-hidden="true" html={tk("final.stampHtml")} />
          <span className="gl-kicker gl-mono" style={{ color: "var(--gl-saffron)" }}>
            <span style={{ display: "inline-block", width: 28, height: 1, background: "var(--gl-saffron)" }} />
            {tk("final.kicker")}
          </span>
          <Html as="h2" html={tk("final.h2Html")} />
          <p>{tk("final.body")}</p>
          <div className="gl-hero-ctas">
            <Link className="gl-btn gl-btn-saffron gl-btn-lg" to="/login">
              {tk("final.cta")} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="gl-footer">
        <div className="gl-wrap">
          <div className="gl-foot-grid">
            <div>
              <Link className="gl-logo" to="/" aria-label={tk("nav.logoAria")}>
                <span className="gl-logo-mark"><img src={iconUrl} width={64} height={64} alt="" decoding="async" loading="lazy" /></span>
                <span className="gl-logo-wordmark">guiri<span className="gl-logo-dot">.</span></span>
              </Link>
              <p className="gl-foot-blurb">{tk("footer.blurb")}</p>
            </div>
            <div className="gl-foot-col">
              <h4>{tk("footer.product")}</h4>
              <ul>
                <li><a href="#how">{tk("nav.how")}</a></li>
                <li><a href="#uses">{tk("nav.uses")}</a></li>
                <li><a href="#pricing">{tk("nav.pricing")}</a></li>
                <li><FAQLink /></li>
              </ul>
            </div>
            <div className="gl-foot-col">
              <h4>{tk("footer.account")}</h4>
              <ul>
                <li><Link to="/login">{tk("footer.goTo")}</Link></li>
                <li>
                  <a
                    className="gl-app-store-link"
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={tk("hero.appStoreAria")}
                  >
                    <img src={APP_STORE_BADGE} alt={tk("hero.appStoreAlt")} width={120} height={40} loading="lazy" decoding="async" />
                  </a>
                </li>
              </ul>
            </div>
            <div className="gl-foot-col">
              <h4>{tk("footer.legal")}</h4>
              <ul>
                <li><Link to="/privacy">{tk("footer.privacy")}</Link></li>
                <li><Link to="/terms">{tk("footer.terms")}</Link></li>
              </ul>
            </div>
          </div>
          <div className="gl-foot-bottom">
            <span>{tk("footer.copy")}</span>
            <span>{tk("footer.builtOn")}</span>
          </div>
        </div>
      </footer>

      {langOpen && (
        <Suspense fallback={null}>
          <LanguageSelectionModal isOpen={langOpen} onLanguageSelected={() => setLangOpen(false)} />
        </Suspense>
      )}
    </div>
  );
};

export default MarketingLanding;
